import type {
  TimelineProject,
  TimelineClip,
  TimelineTransition,
  TimelineAudioTrack,
  TransitionType,
} from "@scroll/shared-types/timeline";
import type {
  PersistedAudioTrackForWire,
  PersistedClipForWire,
  PersistedTimelineProjectForWire,
} from "@/lib/timeline-serialize";

/** Inverse of toPersistedTimelineProject — reconstructs a runtime TimelineProject from
 *  the persisted wire shape. Used by the dev fixture loader so the harness can boot
 *  the editor with a known PersistedTimelineProjectForWire JSON.
 *
 *  This is deliberately lossy:
 *  - Transition ids are synthesized (the persisted shape only carries afterClipIndex).
 *  - textOverlays are always [] (the wire shape doesn't carry them).
 *  - sourceUri for a clip falls back to the persisted source URL or a placeholder.
 *  - The project gets a synthetic id.
 *
 *  Never call this from a production code path — it bypasses upload + capability gating.
 */
export function fromPersistedTimelineProject(
  persisted: PersistedTimelineProjectForWire,
): TimelineProject {
  return {
    id: `fixture-${Date.now()}`,
    version: 1,
    aspectRatio: persisted.aspectRatio,
    outputWidth: persisted.outputWidth,
    outputHeight: persisted.outputHeight,
    clips: persisted.clips.map((c) => clipFromWire(c)),
    transitions: persisted.transitions.map((t, i) => ({
      id: `fixture-transition-${i}`,
      type: t.type as TransitionType,
      duration: t.duration,
    })),
    audioTracks: persisted.audioTracks.map((t) => audioTrackFromWire(t)),
    textOverlays: [],
    ...(persisted.introTransition
      ? {
          introTransition: {
            id: "fixture-intro",
            type: persisted.introTransition.type,
            duration: persisted.introTransition.duration,
          } satisfies TimelineTransition,
        }
      : {}),
    ...(persisted.outroTransition
      ? {
          outroTransition: {
            id: "fixture-outro",
            type: persisted.outroTransition.type,
            duration: persisted.outroTransition.duration,
          } satisfies TimelineTransition,
        }
      : {}),
  };
}

function clipFromWire(c: PersistedClipForWire): TimelineClip {
  const sourceUri =
    c.source?.kind === "blob"
      ? c.source.url
      : c.source?.kind === "mux"
        ? `mux://${c.source.assetId}`
        : `fixture://clip-${c.id}`;
  return {
    id: c.id,
    sourceUri,
    type: c.type,
    sourceDuration: c.sourceDuration,
    sourceWidth: c.sourceWidth,
    sourceHeight: c.sourceHeight,
    ...(c.timelineStart != null ? { timelineStart: c.timelineStart } : {}),
    trimStart: c.trimStart,
    trimEnd: c.trimEnd,
    speed: c.speed,
    volume: c.volume,
    displayDuration: c.displayDuration,
    filter: c.filter,
    ...(c.framing ? { framing: c.framing } : {}),
    ...(c.greenScreen !== undefined
      ? {
          greenScreen: c.greenScreen
            ? {
                enabled: c.greenScreen.enabled,
                mode: "chroma" as const,
                keyColor: c.greenScreen.keyColor,
                similarity: c.greenScreen.similarity,
                blend: c.greenScreen.blend,
                ...(c.greenScreen.spill != null ? { spill: c.greenScreen.spill } : {}),
                ...(c.greenScreen.foregroundFit
                  ? { foregroundFit: c.greenScreen.foregroundFit }
                  : {}),
                ...(c.greenScreen.foregroundPositionX != null
                  ? { foregroundPositionX: c.greenScreen.foregroundPositionX }
                  : {}),
                ...(c.greenScreen.foregroundPositionY != null
                  ? { foregroundPositionY: c.greenScreen.foregroundPositionY }
                  : {}),
                ...(c.greenScreen.foregroundScale != null
                  ? { foregroundScale: c.greenScreen.foregroundScale }
                  : {}),
                background:
                  c.greenScreen.background.type === "color"
                    ? { type: "color", color: c.greenScreen.background.color }
                    : {
                        type: "image",
                        sourceUri: c.greenScreen.background.source.url,
                        blobKey: c.greenScreen.background.source.blobKey,
                        blobUrl: c.greenScreen.background.source.url,
                        fit: "cover" as const,
                      },
              }
            : null,
        }
      : {}),
    ...(c.source?.kind === "blob"
      ? { blobKey: c.source.blobKey, blobUrl: c.source.url }
      : {}),
  };
}

function audioTrackFromWire(t: PersistedAudioTrackForWire): TimelineAudioTrack {
  const sourceUri =
    t.source.kind === "blob"
      ? t.source.url
      : `musicCatalog://${t.source.trackId}`;
  return {
    id: t.id,
    type: t.type,
    sourceUri,
    sourceDuration: t.sourceDuration,
    timelineStart: t.timelineStart,
    trimStart: t.trimStart,
    trimEnd: t.trimEnd,
    volumeKeyframes: t.volumeKeyframes,
    ...(t.musicTrackId != null ? { musicTrackId: t.musicTrackId } : {}),
    ...(t.title ? { title: t.title } : {}),
    ...(t.artist ? { artist: t.artist } : {}),
  };
}

/** Fetch a fixture JSON by name and convert it to a runtime TimelineProject.
 *  Names map to files under /scripts/timeline-fixtures/. Returns null on
 *  network failure or schema mismatch (caller falls back to a fresh editor).
 *
 *  DEV-only — guard the call site with `import.meta.env.DEV` so the network
 *  request never fires in production. */
export async function loadFixtureProjectByName(
  name: string,
): Promise<TimelineProject | null> {
  if (!/^[A-Za-z0-9_-]+$/.test(name)) return null;
  try {
    const res = await fetch(`/scripts/timeline-fixtures/${name}.json`);
    if (!res.ok) return null;
    const persisted = (await res.json()) as PersistedTimelineProjectForWire;
    if (!persisted || persisted.version !== 1) return null;
    return fromPersistedTimelineProject(persisted);
  } catch {
    return null;
  }
}

/** Read ?timeline-fixture=<name> from the current URL. Returns null when absent
 *  or when not running in dev mode. */
export function readFixtureNameFromUrl(): string | null {
  if (!import.meta.env.DEV) return null;
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const name = params.get("timeline-fixture");
  if (!name) return null;
  if (!/^[A-Za-z0-9_-]+$/.test(name)) return null;
  return name;
}
